// MAVIS Builder — Phase 1 workspace (spec intake + plan + critic).
// Read-only. Nothing on this page mutates the repo or creates PRs.
import { useCallback, useEffect, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Wrench,
  ShieldCheck,
  GitBranch,
  Eye,
  Loader2,
  FileSearch,
  Scale,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";

interface RepoCfg { owner?: string | null; repo?: string | null; ref?: string | null }
interface ConnectionStatus { connector_linked: boolean; repo_configured: boolean; config: RepoCfg | null }

interface SpecRow {
  id: string;
  created_at?: string;
  normalized?: {
    intent?: string;
    hard_requirements?: string[];
    must_not_change?: string[];
    open_questions?: string[];
  };
}

interface PlanRow {
  id: string;
  outcome_title: string | null;
  authoritative_source: string | null;
  files_affected: string[];
  tests_required: string[];
  risks: string[];
  will_not_change: string[];
  acceptance_criteria: string[];
  uncertainty: string[];
  plan_body: any;
  critic_verdict: "approved" | "rejected" | "not_reviewed";
  critic_body: any;
  repo_owner: string | null;
  repo_name: string | null;
  ref: string | null;
}

export default function EosBuilderPage() {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [ownerInput, setOwnerInput] = useState("");
  const [repoInput, setRepoInput] = useState("");
  const [refInput, setRefInput] = useState("main");

  const [prompt, setPrompt] = useState("");
  const [spec, setSpec] = useState<SpecRow | null>(null);
  const [plan, setPlan] = useState<PlanRow | null>(null);

  const [intakeBusy, setIntakeBusy] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);
  const [criticBusy, setCriticBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke("eos-builder-github", {
      body: { action: "connection_status" },
    });
    if (error) { toast.error("Could not read connection status."); return; }
    setStatus(data as ConnectionStatus);
    const cfg = (data as ConnectionStatus).config;
    if (cfg) {
      setOwnerInput(cfg.owner ?? "");
      setRepoInput(cfg.repo ?? "");
      setRefInput(cfg.ref ?? "main");
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const saveRepo = async () => {
    if (!ownerInput.trim() || !repoInput.trim()) { toast.error("Owner and repo are required."); return; }
    const { error } = await supabase.functions.invoke("eos-builder-github", {
      body: { action: "set_config", owner: ownerInput.trim(), repo: repoInput.trim(), ref: refInput.trim() || "main" },
    });
    if (error) { toast.error("Save failed."); return; }
    toast.success("Repo settings saved.");
    loadStatus();
  };

  const runIntake = async () => {
    if (!prompt.trim()) { toast.error("Paste an implementation spec first."); return; }
    setIntakeBusy(true); setSpec(null); setPlan(null);
    try {
      const { data, error } = await supabase.functions.invoke("eos-builder-plan", { body: { action: "intake", prompt } });
      if (error) throw error;
      setSpec(data.spec);
      toast.success("Spec normalized.");
    } catch (e: any) {
      toast.error(e.message || "Intake failed.");
    } finally { setIntakeBusy(false); }
  };

  const runPlan = async () => {
    if (!spec) return;
    setPlanBusy(true); setPlan(null);
    try {
      const { data, error } = await supabase.functions.invoke("eos-builder-plan", { body: { action: "plan", spec_id: spec.id } });
      if (error) throw error;
      setPlan(data.plan);
      toast.success("Plan drafted.");
    } catch (e: any) {
      toast.error(e.message || "Plan generation failed.");
    } finally { setPlanBusy(false); }
  };

  const runCritic = async () => {
    if (!plan) return;
    setCriticBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("eos-builder-plan", { body: { action: "critic", plan_id: plan.id } });
      if (error) throw error;
      setPlan(data.plan);
      toast.success(`Critic ${data.plan.critic_verdict}.`);
    } catch (e: any) {
      toast.error(e.message || "Critic review failed.");
    } finally { setCriticBusy(false); }
  };

  return (
    <AdminShell>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-semibold tracking-tight">MAVIS Builder</h1>
              <Badge variant="outline" className="text-xs">Phase 1 · Read-only + Plan</Badge>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Paste a spec. MAVIS inspects the live repository, drafts an implementation plan, and runs a critic pass.
              Nothing is committed. No branches. No PRs. Read-only until Phase 2 is approved.
            </p>
          </div>
        </div>

        {/* Connection + repo settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <GitBranch className="h-4 w-4" /> Repository settings
            </CardTitle>
            <CardDescription>
              GitHub token is stored server-side (Lovable connector). The model never sees it. All reads are audited.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant={status?.connector_linked ? "default" : "destructive"} className="gap-1">
                {status?.connector_linked ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                {status?.connector_linked ? "GitHub connector linked" : "GitHub connector NOT linked"}
              </Badge>
              <Badge variant={status?.repo_configured ? "default" : "outline"} className="gap-1">
                {status?.repo_configured ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                {status?.repo_configured ? "Repo configured" : "Repo not configured"}
              </Badge>
              {!status?.connector_linked && (
                <span className="text-xs text-muted-foreground">
                  Ask an admin to link the GitHub connector with read-only permissions (Metadata: R, Contents: R, PRs: R).
                </span>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="owner">Owner</Label>
                <Input id="owner" value={ownerInput} onChange={e => setOwnerInput(e.target.value)} placeholder="e.g. silversage" />
              </div>
              <div>
                <Label htmlFor="repo">Repo</Label>
                <Input id="repo" value={repoInput} onChange={e => setRepoInput(e.target.value)} placeholder="e.g. hub" />
              </div>
              <div>
                <Label htmlFor="ref">Default ref</Label>
                <Input id="ref" value={refInput} onChange={e => setRefInput(e.target.value)} placeholder="main" />
              </div>
            </div>
            <Button size="sm" onClick={saveRepo}>Save repo settings</Button>
          </CardContent>
        </Card>

        {/* Spec intake */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><FileSearch className="h-4 w-4" /> Paste an implementation spec</CardTitle>
            <CardDescription>Can be a prompt written by you or by ChatGPT. MAVIS will normalize it and use it as ground truth for the plan.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={10} placeholder="Paste the full spec here..." />
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={runIntake} disabled={intakeBusy || !status?.connector_linked || !status?.repo_configured}>
                {intakeBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Normalizing…</> : "Normalize spec"}
              </Button>
              <Button onClick={runPlan} disabled={planBusy || !spec}>
                {planBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Inspecting repo & planning…</> : "Produce plan"}
              </Button>
              <Button variant="secondary" onClick={runCritic} disabled={criticBusy || !plan}>
                {criticBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Critic reviewing…</> : "Run critic"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Normalized spec */}
        {spec && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Normalized spec</CardTitle>
              <CardDescription>{spec.normalized?.intent}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <BulletBlock title="Hard requirements" items={spec.normalized?.hard_requirements} />
              <BulletBlock title="Must not change" items={spec.normalized?.must_not_change} />
              <BulletBlock title="Open questions" items={spec.normalized?.open_questions} />
            </CardContent>
          </Card>
        )}

        {/* Plan */}
        {plan && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base">{plan.outcome_title ?? "Plan"}</CardTitle>
                  <CardDescription>
                    Authoritative source: <span className="font-medium">{plan.authoritative_source ?? "—"}</span> · {plan.repo_owner}/{plan.repo_name}@{plan.ref}
                  </CardDescription>
                </div>
                <CriticBadge verdict={plan.critic_verdict} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <BulletBlock title="What MAVIS found (verified in repo)" items={plan.plan_body?.eos_understanding} />
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Implementation plan</div>
                <ol className="ml-4 list-decimal space-y-1">
                  {(plan.plan_body?.implementation_plan ?? []).map((s: any, i: number) => (
                    <li key={i}><span className="font-medium">{s.step}</span>{s.file ? <> — <code className="rounded bg-muted px-1 text-xs">{s.file}</code></> : null}{s.why ? <span className="text-muted-foreground"> — {s.why}</span> : null}</li>
                  ))}
                </ol>
              </div>
              <BulletBlock title="Files likely affected" items={plan.files_affected} mono />
              <BulletBlock title="Tests required" items={plan.tests_required} />
              <BulletBlock title="Acceptance criteria" items={plan.acceptance_criteria} />
              <BulletBlock title="Will not change" items={plan.will_not_change} />
              <BulletBlock title="Risks" items={plan.risks} />
              <BulletBlock title="Uncertainty / missing repo context" items={plan.uncertainty} />

              {plan.critic_body && (
                <>
                  <Separator />
                  <div>
                    <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <Scale className="h-3.5 w-3.5" /> Critic review
                    </div>
                    <BulletBlock title="Reasons" items={plan.critic_body?.reasons} />
                    <BulletBlock title="Must fix before build" items={plan.critic_body?.must_fix_before_build} />
                    <BulletBlock title="Confirmed findings" items={plan.critic_body?.confirmed_findings} />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Guardrails */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="h-4 w-4" /> Phase 1 guardrails</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
            <Guardrail icon={<Eye className="h-3.5 w-3.5" />} label="Read-only GitHub scopes: Metadata R, Contents R, PRs R" />
            <Guardrail icon={<ShieldCheck className="h-3.5 w-3.5" />} label="GitHub token stays server-side; never in the model context or browser" />
            <Guardrail icon={<Wrench className="h-3.5 w-3.5" />} label="No branches, commits, PRs, workflow edits, secrets access, or admin ops" />
            <Guardrail icon={<GitBranch className="h-3.5 w-3.5" />} label="Every repo read is audited (admin, run, repo, ref, path, query, timestamp)" />
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}

function BulletBlock({ title, items, mono }: { title: string; items?: string[] | null; mono?: boolean }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      <ul className="ml-4 list-disc space-y-1">
        {items.map((it, i) => <li key={i} className={mono ? "font-mono text-xs" : ""}>{it}</li>)}
      </ul>
    </div>
  );
}

function CriticBadge({ verdict }: { verdict: PlanRow["critic_verdict"] }) {
  if (verdict === "approved") return <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600"><CheckCircle2 className="h-3 w-3" /> Critic approved</Badge>;
  if (verdict === "rejected") return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Critic rejected</Badge>;
  return <Badge variant="outline" className="gap-1"><Scale className="h-3 w-3" /> Not reviewed</Badge>;
}

function Guardrail({ icon, label }: { icon: React.ReactNode; label: string }) {
  return <div className="flex items-start gap-2 rounded border bg-muted/30 p-2 text-xs">{icon}<span>{label}</span></div>;
}
